import type {
	PortalCyberSourceCaptureContextRequestDto,
	PortalCyberSourceCaptureContextResponseDto,
	PortalGatewayConfigResponseDto,
	PublicSentFormAddPaymentMethodRequestDto,
	PublicSentFormAddPaymentMethodResponseDto,
	PublicSentFormModel,
	PublicSentFormResponseDto,
	PublicSentFormState,
	PublicSentFormSubmitRequestDto,
	PublicSentFormSubmitResponseDto,
	SentFormPaymentMethodOptionModel,
} from '@supply-mover-2-0/shared';
import { PAYMENT_LINK_TOKEN_HEADER } from '@supply-mover-2-0/shared';
import { getPortalTenantHeader } from '../lib/tenantResolver';

/**
 * PD-13768 — the storefront's client for a sent form's public endpoints. Like the
 * payment-link client: no sign-in, the link token in the X-Payment-Link-Token header
 * (never in an API URL), no storefront cookies sent.
 */
export class SentFormRequestError extends Error {
	public constructor(
		message: string,
		public readonly status: number,
		public readonly state: PublicSentFormState | null,
		public readonly errors: Record<string, string> | null,
	) {
		super(message);
		this.name = 'SentFormRequestError';
	}
}

const baseUrl = (): string => `${import.meta.env.VITE_API_URL}/api/portal/sent-forms/current`;

async function request<TResponse>(
	token: string,
	path: string,
	init: { method: 'GET' | 'POST'; body?: unknown },
): Promise<TResponse> {
	const tenantHeader = getPortalTenantHeader();
	const response = await fetch(`${baseUrl()}${path}`, {
		method: init.method,
		headers: {
			[PAYMENT_LINK_TOKEN_HEADER]: token,
			...(tenantHeader != null ? { 'X-Portal-Tenant': tenantHeader } : {}),
			...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
		},
		body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
		credentials: 'omit',
	});
	const json = (await response.json().catch(() => null)) as
		| (TResponse & { message?: string; state?: PublicSentFormState; errors?: Record<string, string> })
		| null;
	if (!response.ok || json == null) {
		throw new SentFormRequestError(
			json?.message ??
				(response.status === 429
					? 'Too many attempts. Please wait a few minutes and try again.'
					: 'Something went wrong. Please try again.'),
			response.status,
			json?.state ?? null,
			json?.errors ?? null,
		);
	}
	return json;
}

export async function fetchSentForm(token: string): Promise<PublicSentFormModel> {
	return (await request<PublicSentFormResponseDto>(token, '', { method: 'GET' })).data;
}

export async function addSentFormPaymentMethod(
	token: string,
	body: PublicSentFormAddPaymentMethodRequestDto,
): Promise<SentFormPaymentMethodOptionModel> {
	return (
		await request<PublicSentFormAddPaymentMethodResponseDto>(token, '/payment-method', {
			method: 'POST',
			body,
		})
	).data;
}

export async function submitSentForm(
	token: string,
	body: PublicSentFormSubmitRequestDto,
): Promise<string> {
	const response = await request<PublicSentFormSubmitResponseDto>(token, '/submit', {
		method: 'POST',
		body,
	});
	return response.data?.message ?? 'Thank you.';
}

export async function getSentFormCaptureContext(
	token: string,
	paymentType: PortalCyberSourceCaptureContextRequestDto['paymentType'],
): Promise<string> {
	const body: PortalCyberSourceCaptureContextRequestDto = {
		paymentType,
		targetOrigin: window.location.origin,
	};
	const response = await request<PortalCyberSourceCaptureContextResponseDto>(
		token,
		'/cybersource/capture-context',
		{ method: 'POST', body },
	);
	return response.data.captureContext;
}

export function sentFormGatewayConfig(form: PublicSentFormModel): PortalGatewayConfigResponseDto {
	return { success: form.gateway != null, data: form.gateway };
}
